import React from 'react';
import { useRouter } from 'next/router';
import styled, { useTheme } from 'styled-components';
import { Trans } from '@lingui/react';
import { isPast } from 'date-fns/isPast';
import { addDays } from 'date-fns/addDays';
import Card from '@organisms/cards/Card';
import Icon from '@components/Icon';
import PhaseProgramVideoCard from '@organisms/cards/ProgramVideoCard';
import GenreInstructorMetadata from '@ecosystems/GenreInstructorMetadata';
import { CameraIcon } from '@assets/index';
import { getVideoDescription } from '@lib/helpers';
import {
  Instructor,
  ProgramCategory,
  ProgramLiveEventElementFieldsFragment,
} from '@gql/generated';

export const StyledCard = styled(Card)<{ category: ProgramCategory }>`
  padding-left: 0;
  margin-bottom: 20px;
`;

export const ThumbnailWrapper = styled.div<{
  locked?: boolean;
  category: ProgramCategory;
}>`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;

  :before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
  }

  &:hover {
    cursor: pointer;

    & > .changeBackgroundOnHover {
      box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.75);
    }
  }
`;

export const TagsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: start;
  height: 100%;
  width: 100%;
  padding: 10px;
`;

export const VideoTag = styled.span`
  color: ${props => props.theme.colors.neutrals.white};
  border-radius: 3px;
  padding: 5px;
  margin-right: 8px;
  background-color: ${props => props.theme.colors.primary.stone};
`;

const ActionButtonWrapper = styled.div`
  margin-top: 10px;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    margin-top: 0;
  }
`;

const EventLink = styled.a`
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #242424;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 1;
  transition: opacity 300ms ease-in-out;

  :hover {
    opacity: 0.7;
  }

  svg {
    margin-right: 8px;
  }
`;

type Props = {
  element: ProgramLiveEventElementFieldsFragment;
  programSlug: string;
  category: ProgramCategory;
  weekSlug: string;
};

export const ProgramLiveEventCard = ({
  element,
  category,
  programSlug,
  weekSlug,
}: Props) => {
  const router = useRouter();
  const lang = router?.query?.lang as string;
  const theme = useTheme();

  let isMobile = false;
  if (typeof window !== 'undefined') {
    isMobile = window.matchMedia(`(max-width: ${theme.viewports.laptop}px)`)
      .matches;
  }

  const { liveEvent, locked } = element;

  if (category !== ProgramCategory['Courses']) {
    // hide card if all recordings are expired except course programs
    const dates = element?.liveEvent?.eventDates;
    if (dates) {
      const lastDate = dates[dates.length - 1];
      if (isPast(addDays(new Date(lastDate), 3))) {
        return null;
      }
    }
  }

  if (liveEvent?.linkToRecordings?.length > 0) {
    const { title } = element;

    return (
      <>
        {liveEvent?.linkToRecordings?.map(({ video, isExpired }) => {
          if (
            !video?.identifier ||
            (isExpired && category !== ProgramCategory['Courses'])
          ) {
            return null;
          }

          return (
            <PhaseProgramVideoCard
              key={video?.identifier}
              tags={[]}
              locked={locked}
              redirectTo={`/${lang}/${category}/${programSlug}/${weekSlug}/${element?.slug}/${video.identifier}`}
              video={{
                id: video.id,
                identifier: video.identifier,
                title: video.title || title,
                description: getVideoDescription(video),
                thumbnailUrl: video.assets.thumbnailUrl,
                kind: video.kind,
                flavor: video.taxonomies?.flavor?.name,
                publishedAt: video.meta?.publishedAt,
                duration: video.meta.minutes,
                cursorAt: video.cursorAt,
                genreName: video.taxonomies?.genre?.name,
                genreSlug: video.taxonomies?.genre?.slug,
                instructors: video?.taxonomies?.instructors as Instructor[],
              }}
              programCategory={category}
              disableActions={true}
            />
          );
        })}
      </>
    );
  } else {
    const event_url = `/${lang}/live/${liveEvent?.slug}`;

    const actions = (
      <ActionButtonWrapper key="0" data-testid="ActionButtonWrapper">
        <EventLink href={event_url} target="_blank">
          <CameraIcon width={15} /> &nbsp;{' '}
          <Trans id="weeklyProgram.live_event.visit_event" />
        </EventLink>
      </ActionButtonWrapper>
    );

    const url = `/${lang}/${category}/${programSlug}/${weekSlug}/${element?.slug}`;

    return (
      <StyledCard
        data-testid="ProgramLiveCard"
        large={!isMobile}
        redirectTo={{ href: url }}
        thumbnailUrl={liveEvent.thumbnail}
        contentLeftCardLargeSection={[actions]}
        category={category}
        contentAuthor={
          <GenreInstructorMetadata
            instructors={[liveEvent?.primaryInstructor]}
          />
        }
        contentCardLeftSection={actions}
        thumbnailContent={
          <ThumbnailWrapper locked={locked !== false} category={category}>
            <TagsWrapper>
              {locked ? (
                <VideoTag key="lock" data-testid="ProgramVideoCard--Tag">
                  <Icon
                    name="ygb-icon-programlock-ic"
                    color="white"
                    fontSize="17px"
                  />
                </VideoTag>
              ) : null}
              {element?.liveEvent?.userAccess ? (
                <VideoTag>
                  <Trans id="live.event_page.you_r_in_event" />
                </VideoTag>
              ) : (
                <VideoTag>
                  <Trans id="weeklyProgram.live_event.label" />
                </VideoTag>
              )}
            </TagsWrapper>
          </ThumbnailWrapper>
        }
        contentTitle={element?.title || liveEvent.title}
        contentDescription={element?.description}
        isContentDescriptionHTML={true}
      />
    );
  }
};
